import React from "react";
import {
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useDialogInvokers } from "@tiny/lib";
import { Check } from "@tiny/icons";
import useDebounce from "../../hooks/useDebounce";
import { ComponentLoader } from "../../components";
import { formatNumber } from "../../utils/formatUtils";
import {
  usePutCustomerAudienceMembershipQuery,
  usePutSegmentFindFilterQuery,
  usePostSegmentCustomerMutation,
  useDeleteSegmentCustomerMutation,
} from "../../app/useGeneratedApi";
import { useRefreshRegister } from "../../components/RefreshProvider";
import { AudienceMembershipPanelProps } from "./AudienceMembershipPanel.types";
import {
  SEGMENTS_PAGE_SIZE,
  segmentTypePill,
  transformSegmentsToRows,
  transformSuppressionsToRows,
} from "./AudienceMembershipPanel.helpers";

function AddAudienceDialogContent({
  customerId,
  existingSegmentIds,
  onAdded,
  onClose,
}: {
  customerId: string;
  existingSegmentIds: string[];
  onAdded: () => void;
  onClose: () => void;
}) {
  const [search, setSearch] = React.useState("");
  const searchDebounced = useDebounce(search, 400);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data, isLoading } = usePutSegmentFindFilterQuery(
    { searchTextName: searchDebounced, segmentTypes: ["UPLOAD"] },
    { page: 0, size: 20, sort: ["updatedAt,DESC"] },
    { keepPreviousData: true }
  );

  const { mutateAsync: addToSegment } = usePostSegmentCustomerMutation();

  const availableSegments = (data?.content ?? []).filter(
    (segment) => !existingSegmentIds.includes(segment.id)
  );

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((existing) => existing !== id)
        : [...prev, id]
    );
  };

  const handleConfirm = async () => {
    if (selectedIds.length === 0) {
      return;
    }
    setIsSubmitting(true);
    const chosen = availableSegments.filter((segment) =>
      selectedIds.includes(segment.id)
    );
    const results = await Promise.allSettled(
      selectedIds.map((segmentId) => addToSegment({ segmentId, customerId }))
    );
    const succeeded = results.filter(
      (result) => result.status === "fulfilled"
    ).length;
    setIsSubmitting(false);
    if (succeeded > 0) {
      enqueueSnackbar(
        succeeded === 1
          ? `Added to '${chosen[0]?.name}'`
          : `Added to ${succeeded} audiences`,
        { variant: "success" }
      );
      onAdded();
    }
    onClose();
  };

  return (
    <Box sx={{ width: "100%", overflowX: "hidden", py: 2 }}>
      <Typography
        variant="label2R"
        color="grey.80"
        sx={{ display: "block", mb: 5 }}
      >
        Choose one or more audiences to add this contact to.
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder="Search audiences"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ mb: 4 }}
      />
      {isLoading ? (
        <ComponentLoader height="200px" />
      ) : availableSegments.length === 0 ? (
        <Typography
          variant="label2R"
          color="grey.60"
          sx={{ display: "block", py: 10, textAlign: "center" }}
        >
          No matching audiences.
        </Typography>
      ) : (
        <List
          disablePadding
          sx={{
            maxHeight: 320,
            overflowY: "auto",
            pr: 1,
          }}
        >
          {availableSegments.map((segment) => (
            <ListItem
              key={segment.id}
              disableGutters
              onClick={() => toggleSelected(segment.id)}
              sx={{
                py: 3,
                px: 3,
                gap: 3,
                cursor: "pointer",
                borderRadius: 2,
                "&:hover": { backgroundColor: "grey.20" },
              }}
            >
              <Checkbox
                checked={selectedIds.includes(segment.id)}
                tabIndex={-1}
                disableRipple
                sx={{ p: 0, flex: "none" }}
                icon={
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: "5px",
                      border: "1px solid",
                      borderColor: "grey.50",
                      backgroundColor: "common.white",
                    }}
                  />
                }
                checkedIcon={
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: "5px",
                      backgroundColor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Check sx={{ fontSize: 14, color: "common.white" }} />
                  </Box>
                }
              />
              <Typography
                variant="label2R"
                color="grey.100"
                sx={{
                  flex: "0 1 auto",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {segment.name}
              </Typography>
              {segmentTypePill(segment.segmentType)}
              <Typography
                variant="label2R"
                color="grey.60"
                sx={{ ml: "auto", flex: "none", pl: 2 }}
              >
                {formatNumber(segment.numberOfCustomers)}
              </Typography>
            </ListItem>
          ))}
        </List>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={6}
        pt={4}
        sx={{ borderTop: "1px solid", borderColor: "grey.40" }}
      >
        <Typography variant="label2R" color="grey.60">
          {selectedIds.length === 0
            ? "Nothing selected"
            : `${selectedIds.length} selected`}
        </Typography>
        <Box display="flex" gap={3}>
          <Button variant="outlined" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={selectedIds.length === 0 || isSubmitting}
            onClick={handleConfirm}
          >
            {selectedIds.length > 1
              ? `Add to ${selectedIds.length} audiences`
              : "Add to audience"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export function useAudienceMembershipPanel({
  email,
}: AudienceMembershipPanelProps) {
  const { data, isLoading, refetch } = usePutCustomerAudienceMembershipQuery(
    { email: email ?? "" },
    { enabled: !!email }
  );

  const customerId = data?.customerId;
  const audiences = data?.audiences ?? [];

  const segmentRows = React.useMemo(
    () => transformSegmentsToRows(audiences),
    [audiences]
  );
  const suppressionRows = React.useMemo(
    () => transformSuppressionsToRows(data?.suppressions ?? []),
    [data?.suppressions]
  );

  const [segmentsPage, setSegmentsPage] = React.useState(0);
  const segmentsPageCount = Math.max(
    1,
    Math.ceil(segmentRows.length / SEGMENTS_PAGE_SIZE)
  );
  const clampedSegmentsPage = Math.min(segmentsPage, segmentsPageCount - 1);
  const pagedSegmentRows = React.useMemo(
    () =>
      segmentRows.slice(
        clampedSegmentsPage * SEGMENTS_PAGE_SIZE,
        clampedSegmentsPage * SEGMENTS_PAGE_SIZE + SEGMENTS_PAGE_SIZE
      ),
    [segmentRows, clampedSegmentsPage]
  );

  const { requestMediumDialog, exitDialog } = useDialogInvokers();

  const { mutate: removeFromSegment } = useDeleteSegmentCustomerMutation();

  const requestRemoveSegment = React.useCallback(
    (segment: { segmentId: string; segmentName: string }) => {
      if (!customerId) {
        return;
      }
      removeFromSegment(
        { segmentId: segment.segmentId, customerId },
        {
          onSuccess: () => {
            enqueueSnackbar(`Removed from '${segment.segmentName}'`, {
              variant: "success",
            });
            refetch();
          },
        }
      );
    },
    [customerId, removeFromSegment, refetch]
  );

  const openAddSegmentDialog = React.useCallback(() => {
    if (!customerId) {
      return;
    }
    requestMediumDialog({
      titleProps: { children: "Add to audience" },
      contentProps: {
        sx: { borderBottom: "none" },
        children: (
          <AddAudienceDialogContent
            customerId={customerId}
            existingSegmentIds={audiences.map((audience) => audience.segmentId)}
            onAdded={refetch}
            onClose={() => exitDialog()}
          />
        ),
      },
    });
  }, [customerId, audiences, refetch, requestMediumDialog, exitDialog]);

  useRefreshRegister(async () => {
    await refetch();
  });

  return {
    segmentRows,
    pagedSegmentRows,
    segmentsPage: clampedSegmentsPage,
    segmentsPageCount,
    setSegmentsPage,
    suppressionRows,
    suppressed: data?.suppressed ?? false,
    customerId,
    isLoading,
    requestRemoveSegment,
    openAddSegmentDialog,
  };
}
